import type VectorLayer from 'ol/layer/Vector'
import type VectorSource from 'ol/source/Vector'

import { createNonBlockingRenderByTime } from '../../renderingQueue'
import { getLayerMetadata, setLayerMetadata } from '../layerMetadata'

import { findFeature } from '../findFeature'

import { updateDepotFeature } from './utils/updateDepotFeature'
import { findDepotMarker } from './utils/findDepotMarker'

const nonblockingRender = createNonBlockingRenderByTime()

export const updateDepotsMapSelection = async (
  layer: VectorLayer<VectorSource>,
  mapMarkers: uui.domain.ui.map.markers.MapMarkers['depot'],
  mapStyles: uui.domain.ui.map.markers.MapStyles['depot'],
  selection: Set<string>,
) => {
  // retrieve selected items in the current layer
  const layerSelection = (getLayerMetadata(layer, 'selection') ?? new Set()) as Set<string>

  // if prev and next are empty do nothing
  if (selection.size !== 0 || layerSelection.size !== 0) {
    const depotsToDeselect = new Set(layerSelection)

    nonblockingRender.reset()

    for (const markerId of selection) {
      await nonblockingRender.next()

      // remove it from depotId to be deselected
      depotsToDeselect.delete(markerId)

      if (layerSelection.has(markerId)) {
        // depotId was previously selected and it's still selected
        // do nothing
        continue
      }

      const marker = findDepotMarker(mapMarkers, markerId)
      if (!marker) {
        throw new Error(
          `Trying to select Depot marker with lat-lng: ${markerId} for ID: ${markerId} but no feature exist on the map`,
        )
      }

      const feature = findFeature(layer, marker.id)

      if (!feature) {
        throw new Error(
          `Trying to select Depot marker with lat-lng: ${markerId} for ID: ${markerId} but no feature exist on the map`,
        )
      }

      // the depotId is a new selection
      updateDepotFeature(mapStyles, marker, feature, true)
    }

    nonblockingRender.reset()

    for (const markerId of depotsToDeselect) {
      await nonblockingRender.next()

      const marker = findDepotMarker(mapMarkers, markerId)
      if (!marker) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `Trying to deselect Depot marker with lat-lng: ${markerId} for ID: ${markerId} but no feature exist on the map`,
          )
        }
        continue
      }

      const feature = findFeature(layer, marker.id)

      if (!feature) {
        // fail silently in production when deselecting unknown items
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `Trying to deselect Depot marker with lat-lng: ${markerId} for ID: ${markerId} but no feature exist on the map`,
          )
        }

        continue
      }

      // deselect the depot
      updateDepotFeature(mapStyles, marker, feature, false)
    }
  }

  // update the stored collection
  setLayerMetadata(layer, 'selection', selection)
}
